import { useState, useEffect } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import { getAllLevel } from "../../../api/pedagogicElement";
import { getStudentById } from "../../../api/etudiants";
import { getAllAcademicYear } from "../../../api/generalParam";
import { createExternalNote } from "../../../api/externalNote";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { createFileAttachement } from "../../../api/fileAttachement";

//Typage des props parents
type Props = {
  show: boolean;
  metaData: any;
  onClose: () => void;
  onSaved?: () => void;
};

interface Year {
  id: string;
  label: string;
  status: string;
}

interface Level {
  id: string;
  label: string;
  abreviation: string;
}

const CreateExternalNoteModal = ({
  show,
  metaData,
  onClose,
  onSaved,
}: Props) => {
  //Initialisation de l'année academmique actuelle
  const { selectedYear } = useAcademicYear();
  const [allLevels, setAllLevels] = useState<Level[]>([]);
  const [years, setYears] = useState<Year[]>([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<any>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [student, setStudent] = useState<{
    id: string;
    value: string;
    label: any;
  }>();

  //Schéma de notre formulaire
  const createSchema = z.object({
    university: z.string().nonempty("L'établissement est requis"),
    classe: z.string().nonempty("La classe est requise"),
    average: z
      .number({
        invalid_type_error:
          "Doit être un nombre (utilisez le point pour les décimales)",
      })
      .min(0)
      .max(20),
    id_studentProfil: z.string().nonempty("L'étudiant est requis"),
    academicYear: z.string().nonempty("L'année académique est requise"),
    id_level: z.string().nonempty("Le niveau est requis"),
    matricule: z.string().nonempty("Le matricule est requis"),
    pieceJustificatif: z
      .instanceof(File, { message: "La pièce justificative est requise" })
      .refine((file) => file.size > 0, "Le fichier ne doit pas être vide")
      .refine((file) => file.size <= 5_000_000, "Taille max: 5MB")
      .refine(
        (file) =>
          ["application/pdf", "image/jpeg", "image/png"].includes(file.type),
        "Formats acceptés: PDF, JPEG, PNG",
      ),
  });

  type FormFields = z.infer<typeof createSchema>;

  const {
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'une note externe
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const formattedData = {
        ...data,
        id_parcoursStudent: metaData?.id_parcourStudent,
      };
      // console.log("Formatted data: ",formattedData)
      //Envoi des datas pour création vers le backend
      const resp = await createExternalNote(formattedData);

      // console.log('NEW EXTERNAL NOTE: ', resp);
      // creer le fichier justificatif pour la note externe
      const formData = new FormData();
      formData.append("files", data?.pieceJustificatif);
      formData.append("entity_id", resp?.data?.note?.id);
      formData.append("entity_type", "ExternalNote");
      await createFileAttachement(
        formData,
        buildPermissionContext(Resource.REGISTRATION, Action.CREATE),
      );
      reset(); //On réinitialise les champs du formulaire
      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Note externe ajoutée !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur (Toast Error)
      // console.log(error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFiles([file]);
      setValue("pieceJustificatif", file, { shouldValidate: true });
    } else {
      setSelectedFiles([]);
      setValue("pieceJustificatif", new File([], ""), { shouldValidate: true });
    }
  };

  // 👉 Change handler pour le select des Année Académique
  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    const selected = years.find((c: any) => c.id.toString() === selectedId);
    if (selected) setSelectedAcademicYear(selected);
  };

  useEffect(() => {
    // On attend que selectedYear soit défini
    if (!selectedYear?.id || !show) return;

    getAllAcademicYear()
      .then((years) => {
        setYears(years.data);
      })
      .catch((error) => {
        toast.error("Erreur lors de la récupération des années");
      });

    getStudentById(metaData?.id_studentProfil)
      .then((response) => {
        // console.log("Student data: ", response.data);
        const formattedStudent = {
          id: response.data.id,
          value: response.data.id,
          label: (
            <>
              <strong>{response.data.matricule}</strong> -{" "}
              {response.data.firstname} {response.data.lastname}
            </>
          ),
        };
        setStudent(formattedStudent);
        setValue("id_studentProfil", response.data.id);
      })
      .catch((error) => {
        // console.log("error: récupération infos student ", error);
        toast.error("Erreur lors de la récupération de l'étudiant");
      });
  }, [show]);

  /**
   * 👉 Chargement des périodes d'évaluation pour le niveau de la classe sélectionnée
   * - Dépend de selectedClass
   * - Charge toutes les périodes et sélectionne automatiquement la première si dispo
   */
  useEffect(() => {
    if (!selectedAcademicYear?.id) return;

    getAllLevel(
      selectedAcademicYear?.id,
      buildPermissionContext(Resource.EXTERNAL_NOTE, Action.CREATE),
    ).then((res) => {
      const levels = res.data || [];
      // console.log(levels);
      setAllLevels(levels);
    });
  }, [selectedAcademicYear]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Ajouter une note externe</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="level-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Étudiant</label>
                  <br />
                  {student ? <span>{student.label}</span> : null}
                  {errors.id_studentProfil && (
                    <div className="invalid-feedback">
                      {errors.id_studentProfil.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label">Année Universitaire</label>
                  <select
                    {...register("academicYear")}
                    aria-label="Default select example"
                    onChange={handleYearChange}
                    className={
                      errors.academicYear
                        ? "form-select is-invalid"
                        : "form-select"
                    }
                  >
                    <option value={""}>Sélectionner</option>
                    {years &&
                      years
                        .filter((y) => {
                          // Exclure si une archive existe pour cette année
                          const hasArchive = metaData?.studentArchive.some(
                            (archiveItem: any) =>
                              archiveItem.id_academicYear === y.id,
                          );
                          // Exclure si une autre note externe existe pour cette année
                          const hasOtherExternalNote =
                            metaData?.externalNotes.some(
                              (exterNoteItem: any) =>
                                exterNoteItem.id_academicYear === y.id,
                            );
                          return !hasArchive && !hasOtherExternalNote;
                        })
                        .map((year) => (
                          <option key={year.id} value={year.id}>
                            Année {year.label}
                          </option>
                        ))}
                  </select>
                  {errors.academicYear && (
                    <div className="invalid-feedback">
                      {errors.academicYear.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label">Niveau d'Étude</label>
                  <select
                    {...register("id_level")}
                    aria-label="Default select example"
                    className={
                      errors.id_level ? "form-select is-invalid" : "form-select"
                    }
                  >
                    <option value={""}>Sélectionner</option>
                    {allLevels.map((level) => (
                      <option key={level.id} value={level.id}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                  {errors.id_level && (
                    <div className="invalid-feedback">
                      {errors.id_level.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-8 col-xl-8 col-md-8">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Établissement
                  </label>
                  <input
                    {...register("university")}
                    type="text"
                    className={
                      errors.university
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: IAM - ISPRIC"
                  />
                  {errors.university && (
                    <div className="invalid-feedback">
                      {errors.university.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-4 col-xl-4 col-md-4">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    N° Ref
                  </label>
                  <input
                    {...register("matricule")}
                    type="text"
                    className={
                      errors.matricule
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: REF00167"
                  />
                  {errors.matricule && (
                    <div className="invalid-feedback">
                      {errors.matricule.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Classe
                  </label>
                  <input
                    {...register("classe")}
                    type="text"
                    className={
                      errors.classe ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: Informatique, Finance"
                  />
                  {errors.classe && (
                    <div className="invalid-feedback">
                      {errors.classe.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Note Obtenue
                  </label>
                  <input
                    {...register("average", {
                      valueAsNumber: true,
                      validate: {
                        validNumber: (v) => !isNaN(v) || "Nombre requis",
                        min: (v) => v >= 0 || "Minimum 0",
                        max: (v) => v <= 20 || "Maximum 20",
                      },
                    })}
                    step="0.01"
                    min="0"
                    max="20"
                    type="number"
                    className={
                      errors.average
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: 12,5 ou 14.75"
                  />
                  {errors.average && (
                    <div className="invalid-feedback">
                      {errors.average.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="profile-upload mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Pièce(s) Justificatif(s)
                  </label>
                  <div className="profile-uploader d-flex align-items-center">
                    <div className="drag-upload-btn mb-3">
                      Upload
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        //  {...register("pieceJustificatif")}
                        className="form-control image-sign"
                        onChange={handleFileChange}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-danger mb-3 ms-3"
                      onClick={() => {
                        setSelectedFiles([]);
                        setValue("pieceJustificatif", new File([], ""));
                      }}
                    >
                      Supprimer
                    </button>
                  </div>
                  <p className="fs-12">
                    Taille max : 4MB, Formats : JPG, PNG, PDF, DOCx
                  </p>
                  {errors.pieceJustificatif && (
                    <p className="text-red-500 text-sm">
                      {errors.pieceJustificatif.message}
                    </p>
                  )}
                  {selectedFiles.length > 0 && (
                    <ul className="list-unstyled">
                      {selectedFiles.map((file, index) => (
                        <li key={index}>
                          📄 {file.name} — {Math.round(file.size / 1024)} Ko —{" "}
                          {file.type}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="level-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  CreateExternalNoteModal,
  [buildPermission(Resource.EXTERNAL_NOTE, Action.CREATE)],
  "masquer",
);
